/**
 * Unified MSAL + storage teardown for Form Engine Portal logout flows.
 * Single integration point for cross-portal receive, user sign-out, and session-policy expiry.
 *
 * Mirrors Case Management Portal `portalLogout.service.ts`.
 */

import type { AccountInfo, PublicClientApplication } from '@azure/msal-browser';
import {
  AUTH_BROADCAST_CHANNEL_NAMES,
  emitAuthLogoutSignal,
} from '@hsus-dev/cross-portal-logout';
import {
  clearAllSecureStorage,
  clearPriorPortalAuthentication,
  removeSecureItem,
} from '@hsus-dev/session-policy';
import { clearSharedAccessToken } from '../auth/sharedAuth';
import { AUTH_API_CONFIG } from '../config/api.config';
import { SIGNED_OUT_ROUTE_PATH } from '../constants/routeConstants';
import { AUTH_USER_KEY, AUTH_USER_PROFILE_KEY } from '../constants/storageKeyConstants';
import { clearRolesCache } from '../utils/rolesCache.utils';
import { clearRegistrationData } from '../utils/secureRegistrationStorage.utils';
import { clearAllLocalStorage, clearAllSessionStorage } from '../utils/localStorage.utils';
import { clearMsalCacheSynchronously } from '../utils/msalCache.utils';
import { flushExamFormAutoSaveBeforeCrossPortalLogout } from './examFormAutoSaveFlush.service';
import { endSession } from './session.service';
import { msalReady } from './msalBootstrap.service';
import { tokenService } from './token.service';

export type PortalAuthTeardownOptions = {
  /** Notify peer portals and same-origin tabs (default true). */
  notifyPeerPortals?: boolean;
  /** When true, ends Azure AD session via MSAL `logoutRedirect` (user sign-out). */
  terminateIdpSession?: boolean;
  accountForIdpLogout?: AccountInfo | null;
};

async function clearLocalMsalAppSession(msal: PublicClientApplication): Promise<void> {
  const accounts = msal.getAllAccounts();
  const removeAccount = (msal as PublicClientApplication & { removeAccount?: (a: AccountInfo) => void })
    .removeAccount;
  for (const account of accounts) {
    try {
      removeAccount?.(account);
    } catch (error) {
      console.warn('[portalLogout] removeAccount (non-blocking):', error);
    }
  }
  try {
    await msal.clearCache();
  } catch (error) {
    console.warn('[portalLogout] clearCache (non-blocking):', error);
  }
  clearMsalCacheSynchronously(msal, null);
  msal.setActiveAccount(null);
}

async function clearMsalAppSessionSilently(): Promise<void> {
  try {
    const msalInstance = await msalReady;
    if (AUTH_API_CONFIG.providerType === 'msal' && msalInstance) {
      await clearLocalMsalAppSession(msalInstance);
    }
  } catch (error) {
    console.warn('[portalLogout] silent MSAL clear (non-blocking):', error);
  }
}

/**
 * Clears persisted portal auth (tokens, secure storage, roles, browser storage).
 * Does not update React state or call backend logout API.
 */
export async function clearPortalPersistedAuth(options?: {
  notifyPeerPortals?: boolean;
}): Promise<void> {
  const notifyPeerPortals = options?.notifyPeerPortals !== false;

  clearRolesCache();
  clearSharedAccessToken();

  try {
    await tokenService.removeTokens();
    removeSecureItem(AUTH_USER_KEY);
    removeSecureItem(AUTH_USER_PROFILE_KEY);
    clearAllSecureStorage();
    clearRegistrationData();
  } catch (error) {
    console.warn('[portalLogout] token/storage clear (non-blocking):', error);
  }

  if (notifyPeerPortals) {
    try {
      emitAuthLogoutSignal(AUTH_BROADCAST_CHANNEL_NAMES.FORM_ENGINE);
    } catch {
      /* non-blocking */
    }
  }

  try {
    clearAllSessionStorage();
  } catch {
    /* ignore */
  }
  try {
    clearPriorPortalAuthentication();
    clearAllLocalStorage();
  } catch {
    /* ignore */
  }
}

/** App-only partial clear for in-app silent reset (e.g. corrupted storage) — no full browser storage wipe. */
export async function clearPortalPersistedAuthPartial(): Promise<void> {
  clearRolesCache();
  clearSharedAccessToken();

  try {
    await tokenService.removeTokens();
    removeSecureItem(AUTH_USER_KEY);
    removeSecureItem(AUTH_USER_PROFILE_KEY);
    clearAllSecureStorage();
    try {
      localStorage.removeItem('msal_last_account_id');
    } catch (error) {
      console.warn('[portalLogout] Error removing msal_last_account_id (non-blocking):', error);
    }
  } catch (error) {
    console.warn('[portalLogout] partial storage clear (non-blocking):', error);
  }
}

function navigateToSignedOutIfNeeded(): void {
  if (window.location.pathname !== SIGNED_OUT_ROUTE_PATH) {
    window.location.assign(SIGNED_OUT_ROUTE_PATH);
  }
}

/**
 * Shared portal + MSAL teardown used by AuthContext logout (user, session expiry, API-triggered).
 * Caller owns React state, `endSession`, and backend logout API.
 */
export async function performPortalAuthTeardown(options: PortalAuthTeardownOptions): Promise<void> {
  await flushExamFormAutoSaveBeforeCrossPortalLogout();
  await clearPortalPersistedAuth({ notifyPeerPortals: options.notifyPeerPortals });

  if (AUTH_API_CONFIG.providerType === 'msal') {
    const msalInstance = await msalReady;
    if (msalInstance) {
      if (options.terminateIdpSession) {
        await msalInstance.logoutRedirect({
          account: options.accountForIdpLogout ?? undefined,
          postLogoutRedirectUri: `${window.location.origin}${SIGNED_OUT_ROUTE_PATH}`,
        });
        return;
      }
      await clearLocalMsalAppSession(msalInstance);
      return;
    }
  }

  if (options.terminateIdpSession) {
    navigateToSignedOutIfNeeded();
  }
}

/**
 * React to another portal/tab signaling logout (BroadcastChannel + `auth_logout_signal`).
 * App-only teardown — no MSAL `logoutRedirect` and no peer notification (caller is external).
 */
export async function performCrossPortalSilentLogout(): Promise<void> {
  await flushExamFormAutoSaveBeforeCrossPortalLogout();
  endSession('user', { invokeLogoutCallback: false });
  await clearPortalPersistedAuth({ notifyPeerPortals: false });
  await clearMsalAppSessionSilently();
  navigateToSignedOutIfNeeded();
}

/**
 * User-initiated sign-out (session warning modal). Caller should run `endSession` first if timers
 * are still active, or rely on AuthContext orchestration.
 */
export async function performPortalLogoutFromUserAction(
  accountForIdpLogout?: AccountInfo | null,
): Promise<void> {
  await performPortalAuthTeardown({
    notifyPeerPortals: true,
    terminateIdpSession: true,
    accountForIdpLogout,
  });
}
